import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { ConfirmModal } from '../../Rocks/components/ConfirmModal';
import { Quarter } from '../../types';
import { getCurrentQuarter, getCurrentYear } from '../vto.util';
import { useDepartments } from '../../Departments/useDepartments';

interface VTOSelectorProps {
  currentQuarter: Quarter;
  currentYear: number;
  currentDepartmentId?: string | null;
  onLoad: (quarter: Quarter, year: number, departmentId: string | null) => void;
  onCreate: (quarter: Quarter, year: number, departmentId: string | null) => void;
  loading?: boolean;
}

const QUARTERS: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
const YEARS = Array.from({ length: 27 }, (_, i) => 2024 + i);

/**
 * V/TO period selector. Controlled by the parent's current selection; changing any
 * dropdown (Quarter / Year / Department) auto-loads that selection immediately.
 */
export function VTOSelector({
  currentQuarter,
  currentYear,
  currentDepartmentId,
  onLoad,
  onCreate,
  loading = false,
}: VTOSelectorProps) {
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const { departments } = useDepartments();

  const departmentId = currentDepartmentId || null;

  const handleCreateConfirm = () => {
    onCreate(currentQuarter, currentYear, departmentId);
    onCreateModalClose();
  };

  const getDepartmentDisplayName = () => {
    if (!departmentId) return 'Organization-wide';
    const dept = departments.find((d) => d.id === departmentId);
    return dept ? dept.name : 'Unknown Department';
  };

  return (
    <>
      <Box bg="white" rounded="lg" shadow="sm" borderWidth="1px" p={6}>
        <HStack spacing={4} align="flex-end">
          <FormControl flex={1}>
            <FormLabel>Quarter</FormLabel>
            <Select
              value={currentQuarter}
              onChange={(e) => onLoad(e.target.value as Quarter, currentYear, departmentId)}
              isDisabled={loading}
            >
              {QUARTERS.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Year</FormLabel>
            <Select
              value={currentYear}
              onChange={(e) => onLoad(currentQuarter, Number(e.target.value), departmentId)}
              isDisabled={loading}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Department</FormLabel>
            <Select
              value={departmentId || ''}
              onChange={(e) => onLoad(currentQuarter, currentYear, e.target.value || null)}
              isDisabled={loading}
            >
              <option value="">Organization-wide</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <Button
            onClick={onCreateModalOpen}
            isDisabled={loading}
            leftIcon={<AddIcon />}
            colorScheme="blue"
          >
            Create New V/TO
          </Button>
        </HStack>

        <Text mt={4} fontSize="sm" color="gray.600">
          Currently viewing: <Text as="span" fontWeight="semibold">{currentQuarter} {currentYear}</Text>
          {departmentId ? (
            <> - <Text as="span" fontWeight="semibold">{getDepartmentDisplayName()}</Text></>
          ) : (
            <> - <Text as="span" fontWeight="semibold">Organization-wide</Text></>
          )}
        </Text>
      </Box>

      <ConfirmModal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        onConfirm={handleCreateConfirm}
        title="Create New V/TO"
        message={`Are you sure you want to create a new V/TO for ${currentQuarter} ${currentYear}${departmentId ? ` - ${getDepartmentDisplayName()}` : ' (Organization-wide)'}?`}
        confirmText="Create"
        confirmColorScheme="blue"
      />
    </>
  );
}

export { getCurrentQuarter, getCurrentYear };
